import { Shapes } from 'lucide-react';
import { useSubmissionStore } from '@/stores/submissionStore';
import { useTranslation } from '@/lib/i18n';
import { RampShape } from '@gorampe/calc-engine';
import { SectionCard } from './SectionCard';
import { FieldLabel } from '../ui/FieldLabel';
import { ChoiceButton } from '../ui/ChoiceButton';
import { Alert } from '../ui/Alert';

export function Section02Shape() {
  const submission = useSubmissionStore((s) => s.submission);
  const patchConfig = useSubmissionStore((s) => s.patchConfig);
  const { t } = useTranslation();

  const { config } = submission;
  const shape = config.shape;

  const setShapeKind = (kind: 'straight' | 'L90' | 'U180') => {
    const prev = config.shape;

    // Preserve effective total run when switching between shape kinds
    const currentTotal = (() => {
      if (prev.kind === 'straight') return prev.totalRunIn;
      if (prev.kind === 'L90') return prev.arm1RunIn + prev.arm2RunIn;
      if (prev.kind === 'U180') return prev.arm1RunIn + prev.arm2RunIn;
      return config.heightIn * 12;
    })();
    const fallbackTotal = Math.max(currentTotal, config.heightIn * 12, 24);

    let newShape: RampShape;
    switch (kind) {
      case 'straight':
        newShape = { kind: 'straight', totalRunIn: fallbackTotal };
        break;
      case 'L90': {
        const half = Math.max(Math.floor(fallbackTotal / 2), 24);
        newShape = {
          kind: 'L90',
          arm1RunIn: half,
          arm2RunIn: half,
          turnDirection: 'left',
          landingDepthIn: 60,
        };
        break;
      }
      case 'U180': {
        const half = Math.max(Math.floor(fallbackTotal / 2), 24);
        newShape = { kind: 'U180', arm1RunIn: half, landingDepthIn: 60, arm2RunIn: half };
        break;
      }
    }
    patchConfig({ shape: newShape });
  };

  const needsRail = config.heightIn > 24 && config.railType === 'aucun';
  const acceptRail = () => {
    const suggested = config.usage === 'com' ? 'barrotin_mains' : 'barrotin';
    patchConfig({ railType: suggested });
  };

  return (
    <SectionCard num="02" title={t('section.02.title')} icon={<Shapes size={18} strokeWidth={2} />}>
      <FieldLabel>{t('shape.form.label')}</FieldLabel>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <ChoiceButton selected={shape.kind === 'straight'} onClick={() => setShapeKind('straight')}>
          {t('shape.form.straight')}
        </ChoiceButton>
        <ChoiceButton selected={shape.kind === 'L90'} onClick={() => setShapeKind('L90')}>
          {t('shape.form.l90')}
        </ChoiceButton>
        <ChoiceButton selected={shape.kind === 'U180'} onClick={() => setShapeKind('U180')}>
          {t('shape.form.u180')}
        </ChoiceButton>
      </div>

      {shape.kind === 'straight' && (
        <>
          <FieldLabel htmlFor="runIn">{t('shape.run.label')}</FieldLabel>
          <input
            id="runIn"
            type="number"
            inputMode="numeric"
            min={24}
            step={12}
            value={shape.totalRunIn}
            onChange={(e) =>
              patchConfig({ shape: { kind: 'straight', totalRunIn: Number(e.target.value) || 0 } })
            }
            className="w-full px-3 py-2.5 mb-3 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
          />
        </>
      )}

      {shape.kind === 'L90' && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <FieldLabel htmlFor="arm1">{t('shape.arm1.label')}</FieldLabel>
              <input
                id="arm1"
                type="number"
                inputMode="numeric"
                value={shape.arm1RunIn}
                onChange={(e) =>
                  patchConfig({ shape: { ...shape, arm1RunIn: Number(e.target.value) || 0 } })
                }
                className="w-full px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
              />
            </div>
            <div>
              <FieldLabel htmlFor="arm2">{t('shape.arm2.label')}</FieldLabel>
              <input
                id="arm2"
                type="number"
                inputMode="numeric"
                value={shape.arm2RunIn}
                onChange={(e) =>
                  patchConfig({ shape: { ...shape, arm2RunIn: Number(e.target.value) || 0 } })
                }
                className="w-full px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
              />
            </div>
          </div>
          <FieldLabel>{t('shape.l90.turn.label')}</FieldLabel>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <ChoiceButton
              selected={shape.turnDirection === 'left'}
              onClick={() => patchConfig({ shape: { ...shape, turnDirection: 'left' } })}
            >
              {t('shape.l90.turn.left')}
            </ChoiceButton>
            <ChoiceButton
              selected={shape.turnDirection === 'right'}
              onClick={() => patchConfig({ shape: { ...shape, turnDirection: 'right' } })}
            >
              {t('shape.l90.turn.right')}
            </ChoiceButton>
          </div>

          <FieldLabel>{t('shape.l90.landing.label')}</FieldLabel>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <ChoiceButton
              selected={(shape.landingDepthIn ?? 60) === 60}
              onClick={() => patchConfig({ shape: { ...shape, landingDepthIn: 60 } })}
            >
              {t('shape.l90.landing.5x5')}
            </ChoiceButton>
            <ChoiceButton
              selected={(shape.landingDepthIn ?? 60) === 96}
              onClick={() => patchConfig({ shape: { ...shape, landingDepthIn: 96 } })}
            >
              {t('shape.l90.landing.5x8')}
            </ChoiceButton>
          </div>
        </>
      )}

      {shape.kind === 'U180' && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div>
            <FieldLabel htmlFor="arm1u">{t('shape.arm1.label')}</FieldLabel>
            <input
              id="arm1u"
              type="number"
              inputMode="numeric"
              value={shape.arm1RunIn}
              onChange={(e) =>
                patchConfig({ shape: { ...shape, arm1RunIn: Number(e.target.value) || 0 } })
              }
              className="w-full px-2 py-2 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
            />
          </div>
          <div>
            <FieldLabel htmlFor="landing">{t('shape.landing.label')}</FieldLabel>
            <input
              id="landing"
              type="number"
              inputMode="numeric"
              value={shape.landingDepthIn}
              onChange={(e) =>
                patchConfig({ shape: { ...shape, landingDepthIn: Number(e.target.value) || 0 } })
              }
              className="w-full px-2 py-2 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
            />
          </div>
          <div>
            <FieldLabel htmlFor="arm2u">{t('shape.arm2.label')}</FieldLabel>
            <input
              id="arm2u"
              type="number"
              inputMode="numeric"
              value={shape.arm2RunIn}
              onChange={(e) =>
                patchConfig({ shape: { ...shape, arm2RunIn: Number(e.target.value) || 0 } })
              }
              className="w-full px-2 py-2 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm focus-visible:outline-2 focus-visible:outline-[var(--brand-primary,#22D3EE)]"
            />
          </div>
        </div>
      )}

      <FieldLabel>{t('shape.width.label')}</FieldLabel>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <ChoiceButton selected={config.widthIn === 36} onClick={() => patchConfig({ widthIn: 36 })}>
          36"
        </ChoiceButton>
        {/* 48 is the internal value for the commercial width; real Go Rampe product is 43.5"/44" */}
        <ChoiceButton selected={config.widthIn === 48} onClick={() => patchConfig({ widthIn: 48 })}>
          44"
        </ChoiceButton>
      </div>

      <FieldLabel>{t('shape.head_platform.label')}</FieldLabel>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <ChoiceButton
          selected={(config.headPlatformDepthIn ?? 60) === 60}
          onClick={() => patchConfig({ headPlatformDepthIn: 60 })}
        >
          {t('shape.head_platform.5x5')}
        </ChoiceButton>
        <ChoiceButton
          selected={(config.headPlatformDepthIn ?? 60) === 96}
          onClick={() => patchConfig({ headPlatformDepthIn: 96 })}
        >
          {t('shape.head_platform.5x8')}
        </ChoiceButton>
      </div>

      <FieldLabel htmlFor="railType">{t('shape.rail.label')}</FieldLabel>
      <select
        id="railType"
        value={config.railType}
        onChange={(e) => patchConfig({ railType: e.target.value as typeof config.railType })}
        className="w-full px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm"
      >
        <option value="aucun">{t('shape.rail.aucun')}</option>
        <option value="ajoure">{t('shape.rail.ajoure')}</option>
        <option value="barrotin">{t('shape.rail.barrotin')}</option>
        <option value="barrotin_mains">{t('shape.rail.barrotin_mains')}</option>
      </select>

      {needsRail && (
        <Alert kind="warn">
          {t('shape.rail.warn_height')}
          <button onClick={acceptRail} className="block mt-1 underline font-semibold text-current">
            {t('shape.rail.accept', {
              type: t(
                config.usage === 'com' ? 'shape.rail.barrotin_mains' : 'shape.rail.barrotin',
              ).toLowerCase(),
            })}
          </button>
        </Alert>
      )}
    </SectionCard>
  );
}
